import { useState } from 'react';

import { Check, Eye, MoreHorizontal, Trash2, X } from 'lucide-react';

import { PolicyGroupTableRow } from '@/types/service/policyGroup';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

import { ColumnDef } from '@tanstack/react-table';

interface InlineEditCellProps {
  value: string;
  groupId: number;
  onSave: (groupId: number, newName: string) => Promise<void>;
  isUpdating: boolean;
}

const InlineEditCell = ({ value, groupId, onSave, isUpdating }: InlineEditCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = async () => {
    if (editValue.trim() && editValue !== value) {
      await onSave(groupId, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className='flex items-center gap-2'>
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isUpdating}
          className='h-8'
          autoFocus
          onBlur={handleCancel}
        />
        <Button
          size='icon'
          variant='ghost'
          className='h-8 w-8'
          onClick={handleSave}
          disabled={isUpdating || !editValue.trim()}
        >
          <Check className='h-4 w-4 text-green-600' />
        </Button>
        <Button
          size='icon'
          variant='ghost'
          className='h-8 w-8'
          onClick={handleCancel}
          disabled={isUpdating}
        >
          <X className='h-4 w-4 text-red-600' />
        </Button>
      </div>
    );
  }

  return (
    <div
      className='font-medium cursor-pointer hover:text-primary'
      onClick={() => !isUpdating && setIsEditing(true)}
    >
      {value}
    </div>
  );
};

export const createPolicyGroupColumns = (
  onView: (group: PolicyGroupTableRow) => void,
  onDelete: (group: PolicyGroupTableRow) => void,
  onUpdateName: (groupId: number, newName: string) => Promise<void>,
): ColumnDef<PolicyGroupTableRow>[] => [
  {
    accessorKey: 'name',
    header: 'Group Name',
    cell: ({ row }) => {
      const group = row.original;
      return (
        <InlineEditCell
          value={group.name}
          groupId={group.id}
          onSave={onUpdateName}
          isUpdating={group.isUpdating || false}
        />
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'member_count',
    header: 'Members',
    cell: ({ row }) => {
      const count = row.getValue('member_count') as number;
      return (
        <div className='flex items-center gap-2'>
          <span className='inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10'>
            {count} {count === 1 ? 'member' : 'members'}
          </span>
        </div>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: 'created_at',
    header: 'Created At',
    cell: ({ row }) => (
      <div className='text-sm'>{new Date(row.getValue('created_at')).toLocaleString()}</div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated At',
    cell: ({ row }) => (
      <div className='text-sm'>{new Date(row.getValue('updated_at')).toLocaleString()}</div>
    ),
    enableSorting: true,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const group = row.original;
      const isUpdating = group.isUpdating || false;
      const isDeleting = group.isDeleting || false;
      const isDisabled = isUpdating || isDeleting;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0' disabled={isDisabled}>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onView(group)} disabled={isDisabled}>
              <Eye className='mr-2 h-4 w-4' />
              View Group
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(group)}
              disabled={isDisabled}
              className='text-destructive focus:text-destructive'
            >
              <Trash2 className='mr-2 h-4 w-4' />
              Delete Group
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
